import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { ArrowLeft, Download, Users, Mail, Calendar, TrendingUp, LogOut, BookOpen } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";

const RANGE_OPTIONS = [
  { key: 7, label: "7 giorni" },
  { key: 30, label: "30 giorni" },
  { key: 90, label: "90 giorni" },
  { key: 180, label: "6 mesi" },
];

const STATUS_COLORS = { draft: "#9CA3AF", scheduled: "#F59E0B", published: "#10B981" };
const STATUS_LABELS = { draft: "Bozza", scheduled: "Programmata", published: "Pubblicata" };

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const token = localStorage.getItem("tv_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load(days);
  }, [days]);

  const load = async (d) => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        axios.get(`${API_URL}/admin/stats?days=${d}`, auth),
        axios.get(`${API_URL}/admin/vouchers/stats`, auth).catch(() => ({ data: null })),
      ]);
      setStats({ ...r.data, vouchers: v.data });
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tv_admin_token");
        navigate("/admin/login");
      }
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem("tv_admin_token");
    navigate("/admin/login");
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <p className="font-editorial italic text-[#3D2140]/60 text-xl">Sto raccogliendo i tuoi dati…</p>
      </div>
    );
  }

  const { totals, period, timeline, top_resources, status_breakdown } = stats;

  const statusData = Object.entries(status_breakdown).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k,
    value: v,
    color: STATUS_COLORS[k] || "#3D2140",
  }));

  // Format day labels as "12 ago"
  const timelineFmt = timeline.map((t) => {
    const d = new Date(t.day);
    return {
      ...t,
      label: d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }),
    };
  });

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-6 md:p-10" data-testid="admin-stats-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt="" className="h-12 w-12" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768]">— Biblioteca T.A.T.A.</p>
              <h1 className="font-display text-3xl md:text-4xl text-[#3D2140]">Statistiche</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]" data-testid="stats-back-dashboard">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <Link to="/admin/risorse" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]">
              <BookOpen size={14} /> Risorse
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>

        {/* Range selector */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <span className="text-xs uppercase tracking-widest text-[#3D2140]/50 mr-2">Periodo:</span>
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => setDays(r.key)}
              data-testid={`range-${r.key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                days === r.key
                  ? "bg-[#FF2768] text-white"
                  : "bg-white border border-[#3D2140]/15 text-[#3D2140] hover:border-[#FF2768]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard
            testId="kpi-downloads"
            icon={<Download size={16} />}
            label="Download totali"
            value={totals.downloads}
            sub={`${period.downloads_in_period} negli ultimi ${period.days} giorni`}
            color="#FF2768"
          />
          <KpiCard
            testId="kpi-newsletter"
            icon={<Mail size={16} />}
            label="Iscritti Newsletter"
            value={totals.newsletter_total}
            sub={`${period.newsletter_in_period} nuovi nel periodo`}
            color="#81C3C9"
          />
          <KpiCard
            testId="kpi-consents"
            icon={<Users size={16} />}
            label="Consensi Marketing"
            value={totals.consents}
            sub={`${totals.conversion_rate}% tasso conversione`}
            color="#3D2140"
          />
          <KpiCard
            testId="kpi-bookings"
            icon={<Calendar size={16} />}
            label="Prenotazioni"
            value={totals.bookings_total}
            sub={`${totals.resources} risorse in catalogo`}
            color="#F59E0B"
          />
        </div>

        {/* Timeline chart */}
        <div className="bg-white rounded-2xl p-5 md:p-6 border border-[#3D2140]/8 mb-8" data-testid="chart-timeline">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-[#FF2768]" />
            <h2 className="font-display text-2xl text-[#3D2140]">Attività nel tempo</h2>
          </div>
          <p className="text-xs text-[#3D2140]/60 mb-4">Download PDF e iscrizioni newsletter — ultimi {period.days} giorni</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={timelineFmt}>
              <defs>
                <linearGradient id="grad-dl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF2768" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#FF2768" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad-nl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#81C3C9" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#81C3C9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#3D2140" strokeOpacity={0.08} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#3D2140" }} interval={Math.max(0, Math.floor(timelineFmt.length / 12))} />
              <YAxis tick={{ fontSize: 11, fill: "#3D2140" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid rgba(10,43,45,0.15)", borderRadius: 12, fontSize: 12 }}
                labelStyle={{ fontWeight: 600, color: "#3D2140" }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="downloads" name="Download" stroke="#FF2768" strokeWidth={2} fill="url(#grad-dl)" />
              <Area type="monotone" dataKey="newsletter" name="Iscritti Newsletter" stroke="#81C3C9" strokeWidth={2} fill="url(#grad-nl)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Top resources */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-5 md:p-6 border border-[#3D2140]/8" data-testid="chart-top-resources">
            <h2 className="font-display text-2xl text-[#3D2140] mb-2">Risorse più scaricate</h2>
            <p className="text-xs text-[#3D2140]/60 mb-4">Top 10 per numero di download</p>
            {top_resources.every((r) => r.downloads === 0) ? (
              <div className="py-12 text-center">
                <p className="font-editorial italic text-[#3D2140]/60 text-lg">
                  Ancora nessun download. I dati appariranno qui appena le risorse verranno scaricate.
                </p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={top_resources.filter(r => r.downloads > 0)} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3D2140" strokeOpacity={0.08} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis dataKey="title" type="category" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(10,43,45,0.15)", borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="downloads" name="Download" fill="#FF2768" radius={[0, 8, 8, 0]} />
                  <Bar dataKey="consents" name="Consensi" fill="#81C3C9" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Status breakdown */}
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-[#3D2140]/8" data-testid="chart-status">
            <h2 className="font-display text-2xl text-[#3D2140] mb-2">Stato del catalogo</h2>
            <p className="text-xs text-[#3D2140]/60 mb-4">Distribuzione delle {totals.resources} risorse</p>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={55}
                  paddingAngle={4}
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                >
                  {statusData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(10,43,45,0.15)", borderRadius: 12, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed table */}
        <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden" data-testid="table-top-resources">
          <div className="p-5 border-b border-[#3D2140]/8">
            <h2 className="font-display text-2xl text-[#3D2140]">Dettaglio risorse</h2>
            <p className="text-xs text-[#3D2140]/60">Metriche complete per ogni PDF</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF8F5] text-[#3D2140]/70 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-4">Risorsa</th>
                  <th className="text-left p-4">Stato</th>
                  <th className="text-right p-4">Download</th>
                  <th className="text-right p-4">Consensi</th>
                  <th className="text-right p-4">Conversione</th>
                </tr>
              </thead>
              <tbody>
                {top_resources.map((r) => {
                  const cr = r.downloads > 0 ? Math.round((r.consents / r.downloads) * 100) : 0;
                  return (
                    <tr key={r.id} className="border-t border-[#3D2140]/5">
                      <td className="p-4 font-semibold text-[#3D2140]">{r.title}</td>
                      <td className="p-4">
                        <span className="inline-block px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold text-white" style={{ background: STATUS_COLORS[r.status] || "#3D2140" }}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td className="p-4 text-right font-mono">{r.downloads}</td>
                      <td className="p-4 text-right font-mono">{r.consents}</td>
                      <td className="p-4 text-right font-mono text-[#3D2140]/70">{cr}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* VOUCHER STATS */}
        {stats.vouchers && (
          <div className="mt-8">
            <h2 className="font-display text-2xl text-[#3D2140] mb-4">🎁 Voucher — panoramica</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <KpiCard icon="🎁" label="Voucher totali" value={stats.vouchers.total || 0} sub={`${stats.vouchers.by_status?.pending || 0} in attesa`} color="#FF2768" testId="kpi-voucher-total" />
              <KpiCard icon="💰" label="Incassato" value={`€ ${(stats.vouchers.revenue_paid || 0).toFixed(0)}`} sub={`${(stats.vouchers.by_status?.paid || 0) + (stats.vouchers.by_status?.redeemed || 0)} voucher pagati`} color="#10B981" testId="kpi-voucher-revenue" />
              <KpiCard icon="⏳" label="Pipeline" value={`€ ${(stats.vouchers.revenue_pipeline || 0).toFixed(0)}`} sub="da incassare" color="#F59E0B" testId="kpi-voucher-pipeline" />
              <KpiCard icon="✨" label="Riscattati" value={stats.vouchers.by_status?.redeemed || 0} sub="già utilizzati" color="#4E9EA5" testId="kpi-voucher-redeemed" />
            </div>

            {stats.vouchers.by_month?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold mb-4">Andamento mensile</p>
                  <div className="space-y-2">
                    {stats.vouchers.by_month.slice(-6).map((m) => {
                      const max = Math.max(...stats.vouchers.by_month.map((x) => x.count), 1);
                      return (
                        <div key={m.month} className="flex items-center gap-3">
                          <span className="text-xs text-[#3D2140]/70 w-20 font-mono">{m.month}</span>
                          <div className="flex-1 h-6 bg-[#FAF8F5] rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#FF2768] to-[#e91e5e] rounded-full flex items-center justify-end pr-2" style={{ width: `${(m.count / max) * 100}%` }}>
                              <span className="text-xs text-white font-bold">{m.count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold mb-4">Per occasione</p>
                  <div className="space-y-2">
                    {stats.vouchers.by_occasion?.map((o) => {
                      const max = Math.max(...stats.vouchers.by_occasion.map((x) => x.count), 1);
                      return (
                        <div key={o.key} className="flex items-center gap-3">
                          <span className="text-xs text-[#3D2140]/70 w-32 truncate">{o.label}</span>
                          <div className="flex-1 h-5 bg-[#FAF8F5] rounded-full overflow-hidden">
                            <div className="h-full bg-[#4E9EA5] rounded-full flex items-center justify-end pr-2" style={{ width: `${(o.count / max) * 100}%` }}>
                              <span className="text-[10px] text-white font-bold">{o.count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color, testId }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold flex items-center gap-1.5">
        <span style={{ color }}>{icon}</span> {label}
      </p>
      <p className="font-display text-4xl mt-1" style={{ color }}>{value}</p>
      <p className="text-xs text-[#3D2140]/50 mt-2">{sub}</p>
    </div>
  );
}
